<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useConfirm } from '@/composables/useConfirm'

const { state, handle } = useConfirm()

function onKey(e: KeyboardEvent) {
  if (!state.value.open) return
  if (e.key === 'Escape') handle(false)
  if (e.key === 'Enter') handle(true)
}

onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))

watch(() => state.value.open, (open) => {
  document.body.style.overflow = open ? 'hidden' : ''
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-150"
      leave-active-class="transition duration-100"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="state.open"
        class="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
        @click.self="handle(false)"
      >
        <Transition
          enter-active-class="transition duration-150"
          leave-active-class="transition duration-100"
          enter-from-class="opacity-0 scale-95"
          leave-to-class="opacity-0 scale-95"
          appear
        >
          <div
            v-if="state.open"
            class="w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700"
          >
            <div class="flex items-start gap-3">
              <div
                v-if="state.variant === 'danger'"
                class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30"
              >
                <svg class="h-5 w-5 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.008v.008H12v-.008z" />
                </svg>
              </div>
              <div class="flex-1">
                <h3 v-if="state.title" class="text-base font-semibold text-gray-900 dark:text-white">
                  {{ state.title }}
                </h3>
                <p class="text-sm text-gray-600 dark:text-gray-300" :class="state.title ? 'mt-1' : ''">
                  {{ state.message }}
                </p>
              </div>
            </div>
            <div class="mt-6 flex justify-end gap-2">
              <button
                type="button"
                class="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600 dark:hover:bg-gray-600"
                @click="handle(false)"
              >
                {{ state.cancelLabel }}
              </button>
              <button
                type="button"
                class="rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm"
                :class="state.variant === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'"
                @click="handle(true)"
              >
                {{ state.confirmLabel }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
